import type { AppState } from 'src/app-state';
import { t } from 'src/i18n';

/**
 * Basistittel som alltid henger på slutten. Holdt i tråd med <title> i
 * index.html.
 */
const BASE_TITLE = 'Trinnvis';

function withBase(name: string): string {
  const trimmed = name.trim();
  return trimmed.length > 0 ? `${trimmed} – ${BASE_TITLE}` : BASE_TITLE;
}

/**
 * Utleder `document.title` fra gjeldende appState.
 *
 * Poenget (issue #7402) er at nettleserhistorikken / tilbakeknappen skal vise
 * meningsfulle titler i stedet for bare «Trinnvis» på hver eneste side.
 *
 * For konto-sidene brukes `selectedEntityName` (== `breadCrumbSelectedName`):
 * det dypeste leddet i brødsmulestien — entitetsnavnet når vi ser på en
 * entitet, ellers sidenavnet. Faste tilstander (innlogging, registrering,
 * ikke-funnet osv.) får egne oversatte titler.
 */
export function documentTitleForAppState(appState: AppState | undefined): string {
  if (appState === undefined) {
    return BASE_TITLE;
  }
  switch (appState.page) {
    case 'account': {
      const viewModel = appState.viewModel;
      if (viewModel?.loaded) {
        const name = viewModel.selectedEntityName?.trim() || viewModel.currentPageView?.name?.trim() || '';
        return withBase(name);
      }
      // Viewmodellen er ikke klar ennå — behold basistittelen. Når entiteten er
      // lastet trigges en ny appState og dermed en ny tittelberegning.
      return BASE_TITLE;
    }
    case 'dashboard':
      return withBase(t('app.documentTitleDashboard'));
    case 'me':
      return withBase(t('app.documentTitleMe'));
    case 'secured':
      return withBase(t('app.documentTitleSecured'));
    case 'signin':
    case 'noUser':
    case 'link':
      return withBase(t('app.documentTitleSignin'));
    case 'signup':
      return withBase(t('app.documentTitleSignup'));
    case 'notFound':
      return withBase(t('app.documentTitleNotFound'));
    case 'issueMessages':
      return withBase(t('app.documentTitleIssueMessages'));
    case 'screenSetup':
      return withBase(t('app.documentTitleScreenSetup'));
    case 'loading':
      return BASE_TITLE;
  }
}
